import { type ReactNode, type Ref } from "react";
import { cn } from "@/lib/utils";

interface GlassAuthCardProps {
  title: string;
  description?: ReactNode;
  /** Journey and step, in small capitals above the title ("Sign in ·
   * verification"). */
  eyebrow?: string;
  /** A short fact beside the eyebrow ("Link valid 21 h"). */
  aside?: ReactNode;
  /** The account this screen acts on (an `AuthIdentity`). */
  identity?: ReactNode;
  /** Progress through a multi-step journey (a `StepRail`). */
  rail?: ReactNode;
  /** `wide` gives account setup room for the QR code and recovery codes. */
  size?: "default" | "wide";
  error?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  /** Receives the title, so a screen can move focus to it when the card
   * changes under the person. */
  titleRef?: Ref<HTMLHeadingElement>;
}

/**
 * The glass card every screen before the workbench is drawn in. The stage
 * around it carries the page's h1 (the brand headline), so the card title is
 * an h2 on every screen.
 */
export function GlassAuthCard({
  title,
  description,
  eyebrow,
  aside,
  identity,
  rail,
  size = "default",
  error,
  children,
  footer,
  className,
  titleRef,
}: GlassAuthCardProps) {
  return (
    <section
      className={cn(
        "w-full rounded-lg border border-[rgb(var(--starling-glass-border)/0.24)] bg-[rgb(var(--starling-glass-bg)/0.86)] p-5 text-[rgb(var(--starling-text-primary))] backdrop-blur-md sm:p-6",
        size === "wide" ? "max-w-[32.5rem]" : "max-w-[25rem]",
        className,
      )}
      style={{
        boxShadow: "0 0 48px rgb(var(--starling-glass-glow) / 0.2)",
      }}
    >
      <div className="mb-5 grid gap-1.5">
        {(eyebrow || aside) && (
          <div className="flex items-baseline justify-between gap-3 font-mono text-[10.5px] font-medium uppercase tracking-[0.12em]">
            <p className="min-w-0 text-[rgb(var(--starling-brand-teal))]">
              {eyebrow}
            </p>
            {aside && (
              <p className="shrink-0 tracking-[0.08em] text-[rgb(var(--starling-text-tertiary))]">
                {aside}
              </p>
            )}
          </div>
        )}
        <h2
          ref={titleRef}
          tabIndex={titleRef ? -1 : undefined}
          className="text-lg font-semibold leading-6 outline-none [text-wrap:balance]"
        >
          {title}
        </h2>
        {description && (
          <p className="text-sm leading-6 text-[rgb(var(--starling-text-secondary))]">
            {description}
          </p>
        )}
      </div>

      <div className="grid gap-4">
        {identity}
        {rail}
        {error && (
          <div
            role="alert"
            className="rounded-md border border-[rgb(var(--starling-coral)/0.45)] bg-[rgb(var(--starling-coral)/0.12)] px-3 py-2 text-sm text-[rgb(var(--starling-coral))]"
          >
            {error}
          </div>
        )}
        {children}
        {footer && <div className="flex flex-wrap gap-2">{footer}</div>}
      </div>
    </section>
  );
}
